import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Download,
  Image,
  Plus,
  Trash2,
  FileBarChart2,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  generatePrismaSVG,
  computePrismaData,
  downloadSVG,
  downloadAsPNG,
} from '@/features/export/prisma-diagram';
import type { PrismaComputeInput } from '@/features/export/prisma-diagram';
import { useReviews } from '@/features/review/hooks/useReview';
import { usePrismaData } from '@/features/export/hooks/usePrismaData';
import { useTeams } from '@/features/team/hooks/useTeam';

// =========================================
// PrismaGenerator — interactive PRISMA 2020 flow diagram builder
// =========================================

interface PrismaGeneratorProps {
  /** Pre-fill with data from the app (sources, evaluations, etc.) */
  initialData?: Partial<PrismaComputeInput>;
  className?: string;
}

interface ExclusionReasonEntry {
  id: string;
  reason: string;
  count: number;
}

function createId(): string {
  return Math.random().toString(36).slice(2, 10);
}

const DEFAULT_INPUT: PrismaComputeInput = {
  totalIdentifiedDatabases: 0,
  totalIdentifiedOther: 0,
  duplicatesRemoved: 0,
  automationRemoved: 0,
  otherRemoved: 0,
  screeningExcluded: 0,
  retrievalFailed: 0,
  eligibilityExcluded: 0,
  exclusionReasons: [],
  includedStudies: 0,
};

export function PrismaGenerator({
  initialData,
  className,
}: PrismaGeneratorProps) {
  const previewRef = useRef<HTMLDivElement>(null);

  // Review auto-fill
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const { data: teams } = useTeams();
  const firstTeamId = teams?.[0]?.id;
  const { data: reviews } = useReviews(firstTeamId);
  const { data: prismaData } = usePrismaData(selectedReviewId ?? undefined);
  const [autoFilled, setAutoFilled] = useState(false);

  // Apply prisma data when review selected
  useEffect(() => {
    if (prismaData && selectedReviewId) {
      setDbCount(prismaData.totalIdentifiedDatabases);
      setOtherCount(prismaData.totalIdentifiedOther);
      setDupsRemoved(prismaData.duplicatesRemoved);
      setAutoRemoved(prismaData.automationRemoved);
      setOtherRemoved(prismaData.otherRemoved);
      setScreenExcluded(prismaData.screeningExcluded);
      setRetrievalFailed(prismaData.retrievalFailed);
      setEligExcluded(prismaData.eligibilityExcluded);
      setIncludedStudies(prismaData.includedStudies);
      setExclusionReasons(
        (prismaData.exclusionReasons ?? []).map((r) => ({
          id: createId(),
          reason: r.reason,
          count: r.count,
        })),
      );
      setAutoFilled(true);
    }
  }, [prismaData, selectedReviewId]);

  // Form state
  const [dbCount, setDbCount] = useState(
    initialData?.totalIdentifiedDatabases ?? 0
  );
  const [otherCount, setOtherCount] = useState(
    initialData?.totalIdentifiedOther ?? 0
  );
  const [dupsRemoved, setDupsRemoved] = useState(
    initialData?.duplicatesRemoved ?? 0
  );
  const [autoRemoved, setAutoRemoved] = useState(
    initialData?.automationRemoved ?? 0
  );
  const [otherRemoved, setOtherRemoved] = useState(
    initialData?.otherRemoved ?? 0
  );
  const [screenExcluded, setScreenExcluded] = useState(
    initialData?.screeningExcluded ?? 0
  );
  const [retrievalFailed, setRetrievalFailed] = useState(
    initialData?.retrievalFailed ?? 0
  );
  const [eligExcluded, setEligExcluded] = useState(
    initialData?.eligibilityExcluded ?? 0
  );
  const [includedStudies, setIncludedStudies] = useState(
    initialData?.includedStudies ?? 0
  );
  const [quantitative, setQuantitative] = useState<number | undefined>(
    initialData?.quantitativeSynthesis
  );

  const [exclusionReasons, setExclusionReasons] = useState<
    ExclusionReasonEntry[]
  >(
    initialData?.exclusionReasons?.map((r) => ({
      id: createId(),
      ...r,
    })) ?? []
  );

  const [isExporting, setIsExporting] = useState(false);

  // Build input
  const computeInput = useMemo((): PrismaComputeInput => {
    return {
      totalIdentifiedDatabases: dbCount,
      totalIdentifiedOther: otherCount,
      duplicatesRemoved: dupsRemoved,
      automationRemoved: autoRemoved,
      otherRemoved: otherRemoved,
      screeningExcluded: screenExcluded,
      retrievalFailed: retrievalFailed,
      eligibilityExcluded: eligExcluded,
      exclusionReasons: exclusionReasons.map((r) => ({
        reason: r.reason,
        count: r.count,
      })),
      includedStudies: includedStudies,
      quantitativeSynthesis: quantitative,
    };
  }, [
    dbCount,
    otherCount,
    dupsRemoved,
    autoRemoved,
    otherRemoved,
    screenExcluded,
    retrievalFailed,
    eligExcluded,
    exclusionReasons,
    includedStudies,
    quantitative,
  ]);

  // Generate SVG
  const svgContent = useMemo(() => {
    const data = computePrismaData(computeInput);
    return generatePrismaSVG(data);
  }, [computeInput]);

  // Render SVG into the preview container via DOM parsing (safe approach)
  useEffect(() => {
    if (!previewRef.current) return;
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgContent, 'image/svg+xml');
    const svgEl = doc.documentElement;

    // Clear previous content
    previewRef.current.replaceChildren();

    // Only append if it parsed correctly
    const parseError = doc.querySelector('parsererror');
    if (!parseError && svgEl.tagName === 'svg') {
      previewRef.current.appendChild(
        document.importNode(svgEl, true)
      );
    }
  }, [svgContent]);

  // Handlers
  const addExclusionReason = useCallback(() => {
    setExclusionReasons((prev) => [
      ...prev,
      { id: createId(), reason: '', count: 0 },
    ]);
  }, []);

  const updateExclusionReason = useCallback(
    (id: string, field: 'reason' | 'count', value: string | number) => {
      setExclusionReasons((prev) =>
        prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
      );
    },
    []
  );

  const removeExclusionReason = useCallback((id: string) => {
    setExclusionReasons((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const handleDownloadSVG = useCallback(() => {
    downloadSVG(svgContent);
  }, [svgContent]);

  const handleDownloadPNG = useCallback(async () => {
    setIsExporting(true);
    try {
      await downloadAsPNG(svgContent);
    } finally {
      setIsExporting(false);
    }
  }, [svgContent]);

  const handleReset = useCallback(() => {
    setDbCount(DEFAULT_INPUT.totalIdentifiedDatabases);
    setOtherCount(DEFAULT_INPUT.totalIdentifiedOther);
    setDupsRemoved(DEFAULT_INPUT.duplicatesRemoved);
    setAutoRemoved(DEFAULT_INPUT.automationRemoved);
    setOtherRemoved(DEFAULT_INPUT.otherRemoved);
    setScreenExcluded(DEFAULT_INPUT.screeningExcluded);
    setRetrievalFailed(DEFAULT_INPUT.retrievalFailed);
    setEligExcluded(DEFAULT_INPUT.eligibilityExcluded);
    setIncludedStudies(DEFAULT_INPUT.includedStudies);
    setQuantitative(undefined);
    setExclusionReasons([]);
  }, []);

  return (
    <div className={cn('grid grid-cols-1 lg:grid-cols-2 gap-6', className)}>
      {/* Left: Form */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileBarChart2 className="h-4 w-4 text-indigo-500" />
              <CardTitle className="text-base">PRISMA Flow Data</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              onClick={handleReset}
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Reset
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Fill from Review */}
          {reviews && reviews.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Fill from Review</Label>
              <Select
                value={selectedReviewId ?? ''}
                onValueChange={(v) => setSelectedReviewId(v || null)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select a review to auto-fill..." />
                </SelectTrigger>
                <SelectContent>
                  {reviews.map((r) => (
                    <SelectItem key={r.id} value={r.id} className="text-xs">
                      {r.name} ({r.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {autoFilled && (
                <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-200">
                  Auto-filled from review
                </Badge>
              )}
            </div>
          )}

          <Separator />

          {/* Identification */}
          <div>
            <h4 className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-3">
              Identification
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <NumberField
                label="From databases"
                value={dbCount}
                onChange={setDbCount}
              />
              <NumberField
                label="From other sources"
                value={otherCount}
                onChange={setOtherCount}
              />
            </div>
          </div>

          <Separator />

          {/* Removal before screening */}
          <div>
            <h4 className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-3">
              Removed before screening
            </h4>
            <div className="grid grid-cols-3 gap-3">
              <NumberField
                label="Duplicates"
                value={dupsRemoved}
                onChange={setDupsRemoved}
              />
              <NumberField
                label="Automation"
                value={autoRemoved}
                onChange={setAutoRemoved}
              />
              <NumberField
                label="Other reasons"
                value={otherRemoved}
                onChange={setOtherRemoved}
              />
            </div>
          </div>

          <Separator />

          {/* Screening */}
          <div>
            <h4 className="text-xs font-semibold text-sky-600 uppercase tracking-wide mb-3">
              Screening
            </h4>
            <NumberField
              label="Records excluded at screening"
              value={screenExcluded}
              onChange={setScreenExcluded}
            />
          </div>

          <Separator />

          {/* Retrieval */}
          <div>
            <h4 className="text-xs font-semibold text-teal-600 uppercase tracking-wide mb-3">
              Retrieval
            </h4>
            <NumberField
              label="Reports not retrieved"
              value={retrievalFailed}
              onChange={setRetrievalFailed}
            />
          </div>

          <Separator />

          {/* Eligibility */}
          <div>
            <h4 className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-3">
              Eligibility
            </h4>
            <NumberField
              label="Reports excluded at eligibility"
              value={eligExcluded}
              onChange={setEligExcluded}
            />
            <div className="mt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-slate-500">
                  Exclusion reasons
                </Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs"
                  onClick={addExclusionReason}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add reason
                </Button>
              </div>
              {exclusionReasons.length === 0 && (
                <p className="text-xs text-slate-400 italic">
                  No exclusion reasons added
                </p>
              )}
              <div className="space-y-2">
                {exclusionReasons.map((er) => (
                  <div key={er.id} className="flex items-center gap-2">
                    <Input
                      placeholder="Reason..."
                      value={er.reason}
                      onChange={(e) =>
                        updateExclusionReason(er.id, 'reason', e.target.value)
                      }
                      className="h-8 text-xs flex-1"
                    />
                    <Input
                      type="number"
                      min={0}
                      value={er.count}
                      onChange={(e) =>
                        updateExclusionReason(
                          er.id,
                          'count',
                          parseInt(e.target.value) || 0
                        )
                      }
                      className="h-8 text-xs w-20"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-slate-400 hover:text-rose-500 shrink-0"
                      onClick={() => removeExclusionReason(er.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Separator />

          {/* Included */}
          <div>
            <h4 className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-3">
              Included
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <NumberField
                label="Studies included"
                value={includedStudies}
                onChange={setIncludedStudies}
              />
              <NumberField
                label="Quantitative synthesis (opt)"
                value={quantitative ?? 0}
                onChange={(v) =>
                  setQuantitative(v === 0 ? undefined : v)
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Right: Preview + Download */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">Preview</CardTitle>
              <Badge variant="outline" className="text-xs">
                PRISMA 2020
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={handleDownloadSVG}
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    SVG
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Download as SVG</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={handleDownloadPNG}
                    disabled={isExporting}
                  >
                    <Image className="h-3.5 w-3.5 mr-1.5" />
                    PNG
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Download as PNG (2x)</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div
            ref={previewRef}
            className="border rounded-lg bg-white overflow-auto max-h-[600px]"
          />
        </CardContent>
      </Card>
    </div>
  );
}

// =========================================
// NumberField helper
// =========================================

interface NumberFieldProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
}

function NumberField({ label, value, onChange }: NumberFieldProps) {
  return (
    <div>
      <Label className="text-xs text-slate-500 mb-1 block">{label}</Label>
      <Input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        className="h-8 text-sm"
      />
    </div>
  );
}
